"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "@/app/i18n/client";
import { LocaleName } from "@/app/i18n/settings";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { SendFeedback } from "@/http/LiveHttpService";
import { UserWithCompany } from "@/types/user";
import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import { Trans } from "react-i18next/TransWithoutContext";
import { makeZodI18nMap } from "@daubit/zod-i18n-map";
import { z } from "zod/v4";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { InputFormField } from "@/app/[lng]/_components/Forms/input-form-field";
import { TextAreaFormField } from "@/app/[lng]/_components/Forms/textarea-form-field";
import { CheckboxFormField } from "@/app/[lng]/_components/Forms/checkbox-form-field";
import { FormField } from "@/components/ui/form";
import { zodCustomErrorsConst } from "@/resources";
import { zodPhoneNumberOptional } from "@/types/zodRegisterConsts";
import { InfoBox } from "@/app/[lng]/_components/info-box";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { MdEmail, MdPerson, MdPhone, MdShortText } from "react-icons/md";
import { getLocalizedRoute } from "@/utils/localized-route";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
const zodContactData = z.object({
  email: z.email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  telephone: zodPhoneNumberOptional,
  message: z.string().min(1),
  dataProtection: z.boolean().refine(v => !!v, {
    params: {
      i18n: zodCustomErrorsConst.DataProtectionAgreement
    }
  })
});
export function ContactForm(t0) {
  const $ = _c(85);
  const {
    data,
    lng
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["common", "zod", "registerData", "zodCustomErrors"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  z.config(makeZodI18nMap({
    t,
    ns: ["zod", "zodCustomErrors"]
  }));
  const user = data?.user;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = zodResolver(zodContactData);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = user?.email;
  const t4 = user?.firstname;
  const t5 = user?.lastname;
  let t6;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      mode: "onChange",
      reValidateMode: "onChange",
      resolver: t2,
      defaultValues: {
        email: t3,
        firstName: t4,
        lastName: t5,
        dataProtection: false
      }
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const formMethods = useForm(t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      mutationFn: _temp
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const {
    isPending,
    isSuccess,
    mutate
  } = useMutation(t7);
  let t8;
  if ($[7] !== mutate) {
    t8 = function onSubmit(values_0) {
      sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.contact, AnalyticsLabels.button, AnalyticsActions.submitFormClick);
      mutate(values_0);
    };
    $[7] = mutate;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const onSubmit = t8;
  const {
    handleSubmit,
    formState: t9
  } = formMethods;
  const {
    errors
  } = t9;
  let t10;
  if ($[9] !== lng) {
    t10 = getLocalizedRoute(lng, "dataprotection");
    $[9] = lng;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const dataProtectionLink = t10;
  let t11;
  if ($[11] !== t) {
    t11 = t("CONTACT.MAIN.SUBTITLE.TEXT");
    $[11] = t;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11) {
    t12 = <h1>{t11}</h1>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== handleSubmit || $[16] !== onSubmit) {
    t13 = handleSubmit(onSubmit);
    $[15] = handleSubmit;
    $[16] = onSubmit;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== errors.firstName?.message || $[19] !== t) {
    t14 = t15 => {
      const {
        field
      } = t15;
      return <InputFormField {...field} label={t("CONTACT.FIRSTNAME.LABEL.TEXT") ?? "Firstname"} icon={<div className="icon"><MdPerson /></div>} error={errors.firstName?.message} />;
    };
    $[18] = errors.firstName?.message;
    $[19] = t;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== formMethods.control || $[22] !== t14) {
    t15 = <FormField control={formMethods.control} name="firstName" render={t14} />;
    $[21] = formMethods.control;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== errors.lastName?.message || $[25] !== t) {
    t16 = t17 => {
      const {
        field: field_0
      } = t17;
      return <InputFormField {...field_0} label={t("CONTACT.LASTNAME.LABEL.TEXT") ?? "Lastname"} icon={<div className="icon"><MdPerson /></div>} error={errors.lastName?.message} />;
    };
    $[24] = errors.lastName?.message;
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== formMethods.control || $[28] !== t16) {
    t17 = <FormField control={formMethods.control} name="lastName" render={t16} />;
    $[27] = formMethods.control;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t15 || $[31] !== t17) {
    t18 = <div className="table-row-mb">{t15}{t17}</div>;
    $[30] = t15;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== errors.email?.message || $[34] !== t) {
    t19 = t20 => {
      const {
        field: field_1
      } = t20;
      return <InputFormField {...field_1} label={t("CONTACT.EMAIL.LABEL.TEXT") ?? "Email"} icon={<div className="icon"><MdEmail /></div>} error={errors.email?.message} />;
    };
    $[33] = errors.email?.message;
    $[34] = t;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== formMethods.control || $[37] !== t19) {
    t20 = <FormField control={formMethods.control} name="email" render={t19} />;
    $[36] = formMethods.control;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== errors.telephone?.message || $[40] !== t) {
    t21 = t22 => {
      const {
        field: field_2
      } = t22;
      return <InputFormField {...field_2} label={t("CONTACT.PHONE.LABEL.TEXT") ?? "Telephone"} icon={<div className="icon"><MdPhone /></div>} error={errors.telephone?.message} />;
    };
    $[39] = errors.telephone?.message;
    $[40] = t;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== formMethods.control || $[43] !== t21) {
    t22 = <FormField control={formMethods.control} name="telephone" render={t21} />;
    $[42] = formMethods.control;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t20 || $[46] !== t22) {
    t23 = <div className="table-row-mb">{t20}{t22}</div>;
    $[45] = t20;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== errors.message?.message || $[49] !== t) {
    t24 = t25 => {
      const {
        field: field_3
      } = t25;
      return <TextAreaFormField {...field_3} label={t("CONTACT.MESSAGE.LABEL.TEXT") ?? "Description"} icon={<div className="icon"><MdShortText /></div>} error={errors.message?.message} />;
    };
    $[48] = errors.message?.message;
    $[49] = t;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== formMethods.control || $[52] !== t24) {
    t25 = <div className="table-row-mb"><FormField control={formMethods.control} name="message" render={t24} /></div>;
    $[51] = formMethods.control;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== dataProtectionLink || $[55] !== errors.dataProtection?.message || $[56] !== t) {
    t26 = t27 => {
      const {
        field: t28
      } = t27;
      const {
        value,
        onChange,
        ...field_4
      } = t28;
      return <CheckboxFormField text={<Trans key="contact-form-checkbox-label-text" i18nKey="CONTACT.CONFIRM.LABEL.TEXT" components={[<a href={dataProtectionLink} />]} t={t} />} {...field_4} onCheckedChange={onChange} checked={value} error={errors.dataProtection?.message} />;
    };
    $[54] = dataProtectionLink;
    $[55] = errors.dataProtection?.message;
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== formMethods.control || $[59] !== t26) {
    t27 = <div className="table-row-mb"><FormField control={formMethods.control} name="dataProtection" render={t26} /></div>;
    $[58] = formMethods.control;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t) {
    t28 = t("CONTACT.MAIN.SUBMIT.TEXT");
    $[61] = t;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== isPending || $[64] !== t28) {
    t29 = <Button className="w-full" type="submit" disabled={isPending} size="lg">{t28}</Button>;
    $[63] = isPending;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== isSuccess || $[67] !== t) {
    t30 = isSuccess && <p data-testid="success-message" className="mb-0"><IoMdCheckmarkCircleOutline size={24} className="mr-2 align-text-bottom text-matchbus" />{t("CONTACT.MESSAGE.SUCCESSFULLSUBMIT.TEXT")}{" "}</p>;
    $[66] = isSuccess;
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t29 || $[70] !== t30) {
    t31 = <div className="contact-form-message-field text-center">{t29}{t30}</div>;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t13 || $[73] !== t18 || $[74] !== t23 || $[75] !== t25 || $[76] !== t27 || $[77] !== t31) {
    t32 = <form className="" onSubmit={t13}>{t18}{t23}{t25}{t27}{t31}</form>;
    $[72] = t13;
    $[73] = t18;
    $[74] = t23;
    $[75] = t25;
    $[76] = t27;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== t12 || $[80] !== t32) {
    t33 = <InfoBox heading={t12} bgColor="bg-white">{t32}</InfoBox>;
    $[79] = t12;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== formMethods || $[83] !== t33) {
    t34 = <FormProvider {...formMethods}>{t33}</FormProvider>;
    $[82] = formMethods;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  return t34;
}
async function _temp(values) {
  const startTime = new Date().getTime();
  const result = await SendFeedback(values);
  const endTime = new Date().getTime();
  const timeDifference = endTime - startTime;
  sendGTMEvent(AnalyticsEventType.Timing, AnalyticsCategories.contact, AnalyticsLabels.SendFeedback, AnalyticsActions.sendData, {
    value: String(timeDifference)
  });
  if (result.successful) {
    sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.contact, AnalyticsLabels.user, AnalyticsActions.formSubmitted);
    return true;
  } else {
    sendGTMEvent(AnalyticsEventType.Error, AnalyticsCategories.contact, AnalyticsLabels.encounteredHTTPError, AnalyticsActions.submitFormClick, {
      fatal: String(false)
    });
    throw new Error(result.error);
  }
}